// LiveEdit provenance registry.
//
// Populated by the CMS localization resolver: maps a rendered string to the
// source(s) it came from (localization key / bundle key / CMS field), so the
// live-edit overlay can resolve a clicked text back to what to save.
//
// Recording is always on (cheap Map writes) so provenance is captured at the
// moment content resolves — the overlay just reads it later.

const entries = new Map() // trimmedValue -> Array<source>

// Parse "<Type>-<field>-<id>" → { contentId, field }. The id is a trailing
// 24-hex Mongo id (possibly followed by extra ids we ignore).
function parseKey(localizationKey) {
  if (typeof localizationKey !== "string") return {}
  const m = localizationKey.match(/^([A-Za-z]+)-(.+?)-([a-f0-9]{24})/)
  if (!m) return {}
  return { type: m[1], field: m[2], contentId: m[3] }
}

const sameSource = (a, b) =>
  a.localizationKey === b.localizationKey &&
  a.bundleKey === b.bundleKey &&
  a.contentId === b.contentId &&
  a.field === b.field

export const LiveEditRegistry = {
  register(value, source) {
    const v = typeof value === "string" ? value.trim() : ""
    if (!v) return
    const src = { ...source, ...parseKey(source.localizationKey) }
    const arr = entries.get(v) || []
    if (!arr.some((s) => sameSource(s, src))) arr.push(src)
    entries.set(v, arr)
  },

  // Record every localized field of a resolved CMS item.
  registerItem(localizationKey, field, finalValue) {
    if (typeof finalValue === "string") {
      this.register(finalValue, { localizationKey, field })
    } else if (finalValue && typeof finalValue === "object" && !Array.isArray(finalValue)) {
      // TextBundle object: { bundleKey: "value", ... }
      for (const [bundleKey, v] of Object.entries(finalValue)) {
        if (typeof v === "string") this.register(v, { localizationKey, field, bundleKey })
      }
    }
  },

  lookup(value) {
    const v = typeof value === "string" ? value.trim() : ""
    return entries.get(v) || []
  },

  // After a successful edit, remap old value → new value.
  remap(oldValue, newValue, source) {
    const ov = String(oldValue).trim()
    const nv = String(newValue).trim()
    const arr = entries.get(ov)
    if (arr) {
      const rest = arr.filter((s) => !sameSource(s, source))
      if (rest.length) entries.set(ov, rest)
      else entries.delete(ov)
    }
    if (nv) this.register(nv, source)
  },
}

export default LiveEditRegistry
